import { Injectable } from '@angular/core';
import { Settings } from '../../../core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import * as moment from 'moment';
import { map } from 'rxjs/operators';
import { SamplesInfo, RegisteredSampleInfo } from '../interface/sample-info';
import { GridHeader, ActionType } from '../../../sample-tracking/tracking-overview/interfaces/grid';
import { DateRangeFilterValue } from '../../../shared/time-frame-filter/timeframe-filter.component';
import { ReturnContainerInfo } from '../../register-samples/register-sample-info';
import { AccessionDetailsService } from '../../register-samples/service/accession-details-service';
import { RegisterSampleStorage } from '../../register-samples/register-sample-storage';

@Injectable()
export class SampleDetailsService {

    public sampleManagementUrl = `${this.settings.apiUrl}/investigator/sampleregistration`;
    public showSubmitAlert: Observable<boolean>;

    constructor(
        private settings: Settings,
        private accessionService: AccessionDetailsService,
        private registerSampleStorage: RegisterSampleStorage,
        private http: HttpClient) {
    }

    public getPendingRegistrationList(): Observable<any> {
        return this.http.get(`${this.sampleManagementUrl}/pendingregistration`).pipe(map((result: any) => {
            return this.formatPendingSamplesList(result.sampleRegistrationPendingListData);
        }));
    }

    public getRegisteredSamplesList(dateBegin: string, dateEnd: string): Observable<any> {
        const getLanguage = () => (navigator.languages && navigator.languages.length && navigator.languages[0]) || navigator.language || 'en';
        return this.http.post(`${this.sampleManagementUrl}/getregisteredsamples`, { fromDate: dateBegin, toDate: dateEnd, userLocale: getLanguage() }).pipe(map((result: any) => {
            return this.formatRegisteredSampleList(result.sampleRegistrationRegisteredSampleData);
        }));
    }

    public applySearch(term: string, header: GridHeader[], gridData: SamplesInfo[]) {
        term = term.toLowerCase().trim();
        let filterData: SamplesInfo[] = [];
        let searchEnabledColumns = header.filter(t => t.enableSearch);
        for (var rowData of gridData) {
            for (var col of searchEnabledColumns) {
                if (`${rowData[col.key as keyof SamplesInfo]}`.toLowerCase().indexOf(term) !== -1) {
                    filterData.push(rowData);
                    break;
                }
            }
        }
        return filterData;
    }

    public getMonthlyRange(): DateRangeFilterValue {
        return {
            dateBegin: moment().subtract(30, 'd').format('YYYY-MM-DD'),
            dateEnd: moment().format('YYYY-MM-DD'),
            rangeType: 'monthly'
        };
    }

    public async OpenDeleteSampleRegistration(accessionNo: string, registedVisitId: number, isYesButtonClicked: boolean) {
        let result = await this.accessionService.deleteRegistrationReasonDialog(accessionNo, isYesButtonClicked).toPromise();
        if (result.isCancelled) { return of(false); }
        let deleteReason = result.selectedOption.confirmOptions;
        let request = {
            registeredVisitId: registedVisitId,
            deleteReason: deleteReason,
            deletedOn: `${moment().local().format('YYYY-MM-DD hh:mm:ss Z')}`
        };
        return this.registerSampleStorage.deleteSampleRegistration(request).pipe(map(() => {
            return true;
        }));
    }

    public getPendingRegisterationHeader(): GridHeader[] {
        let headerList = [
            {
                key: 'accessionNumber',
                displayText: 'Accession',
                enableSearch: true,
            },
            {
                key: 'protocolCode',
                displayText: 'Protocol',
                enableSearch: true,
            },
            {
                key: 'subjectNumber',
                displayText: 'Subject ID',
                enableSearch: true,
            },
            {
                key: 'visitName',
                displayText: 'Visit Name',
                enableSearch: true,
            },
            {
                key: 'collectionDateTime',
                displayText: 'Collection Date & Time',
            },
            {
                key: 'lastSavedDate',
                displayText: 'Last Saved Date',
            },
            {
                key: 'containers',
                displayText: 'Number of Samples Registered',
            },
            {
                displayText: 'Action',
                key: 'null',
                hideSort: true,
                actions: [
                    {
                        actionType: ActionType.resume,
                        cssClass: 'action resume',
                        description: 'Resume Registration',
                        helpTextClass: 'action helptext'
                    },
                    {
                        actionType: ActionType.delete,
                        cssClass: 'action delete',
                        description: 'Delete Registration',
                        helpTextClass: 'action helptext'
                    },
                ]
            },
        ];
        return headerList.map(t => t);
    }


    public getRegisteredSamplesHeader(): GridHeader[] {
        let headerList = [
            {
                key: 'subjectId',
                displayText: 'Subject ID',
                enableFilter: true,
                defaultValue: ' ',
                enableSearch: true,
            },
            {
                key: 'investigatorSiteNameNumber',
                displayText: 'Investigator Number/Name',
                enableFilter: true,
                defaultValue: ' ',
                enableSearch: true,
            },
            {
                key: 'visitDescription',
                displayText: 'Visit',
                enableFilter: true,
                enableSearch: true,
                defaultValue: ' ',
                sort: 'desc',
            },
            {
                key: 'containerIdentifier',
                displayText: 'Sample',
                enableFilter: true,
                defaultValue: ' ',
                enableSearch: true,
                showIndicator: 'showIndicator',
                indicatorField: 'indicator',
                cssClasses: [
                    {
                        key: 'Alt. Container',
                        class: 'alternateContainer'
                    }
                ]
            },
            {
                key: '',
                displayText: 'Container Label',
                defaultValue: ' ',
                subFields: [{ key: 'containerLabel1' }, { key: 'containerLabel2' }],
                enableFilter: true,
                enableSearch: true,
            },
            {
                key: 'expectedReturnCondition',
                displayText: 'Expected Return Condition',
                enableFilter: true,
                defaultValue: ' ',
                enableSearch: true,
            },
            {
                key: 'siteProcessingStatus',
                displayText: 'Status',
                enableFilter: true,
                defaultValue: ' ',
                enableSearch: true,
            },
            {
                key: 'protocolCode',
                displayText: 'Protocol',
                enableFilter: true,
                defaultValue: ' ',
                enableSearch: true,
            },

            {
                key: 'accessionNumber',
                displayText: 'Accession',
                enableFilter: true,
                enableSearch: true,
                defaultValue: ' ',
            },
            {
                key: 'collectionDateTime',
                displayText: 'Date Collected',
                enableFilter: true,
                enableSearch: true,
                defaultValue: ' ',
            },
            {
                key: 'null',
                displayText: 'Actions',
                hideSort: true,
                importAction: true,
                actions: [
                    {
                        description: 'Lab Manual Instructions',
                        actionType: ActionType.none,
                        cssClass: 'action none',
                        displayText: 'Lab Manual Instructions',
                        helpTextClass: 'action helptext'
                    },
                    {
                        description: 'View',
                        actionType: ActionType.view,
                        cssClass: 'action view',
                        displayText: 'View',
                        helpTextClass: 'action helptext'
                    },
                ]
            }
        ];
        return headerList.map(t => t);
    }

    private formatPendingSamplesList(data: SamplesInfo[]) {
        if (!data || !data.length) { return []; }
        return data.map(t => {
            t.collectionDateTime = moment(t.collectionDateTime).isValid ? moment(t.collectionDateTime).format('DD-MMM-YYYY HH:mm') : '';
            t.lastSavedDate = moment(t.lastSavedDate).isValid ? moment(t.lastSavedDate).format('DD-MMM-YYYY') : '';
            t.containers = t.numberOfContainersRegistered + '/' + t.totalNumberOfContainers;
            return t;
        });
    }

    private formatRegisteredSampleList(data: RegisteredSampleInfo[]) {
        if (!data || !data.length) { return []; }
        return data.map(t => {
            t.collectionDateTime = moment(t.collectionDateTime).isValid ? moment(t.collectionDateTime).format('DD-MMM-YYYY HH:mm') : '';
            if (t.isAlternateContainer) {
                let alternateData: RegisteredSampleInfo = {
                    indicator: 'Alt. Container',
                    showIndicator: true,
                    containerIdentifier: t.alternateContainerDescription
                };
                t.children = [alternateData];
            }
            if (t.isAdditionalContainer) {
                t.containerIdentifier = t.barcodeNumber;
                t.showIndicator = true;
                t.indicator = 'Additional';
            }
            return t;
        });
    }
}